import React from 'react';
import {
  HEALTH_SECTIONS,
  HealthSectionId,
  type HealthSection,
} from './healthManagementNav';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface HealthSectionNavProps {
  section: HealthSectionId;
  view: string;
  counts?: Partial<Record<string, number>>;
  onSectionChange: (section: HealthSectionId, view?: string) => void;
}

export default function HealthSectionNav({
  section,
  view,
  counts = {},
  onSectionChange,
}: HealthSectionNavProps) {
  const activeSection = HEALTH_SECTIONS.find((s) => s.id === section) ?? HEALTH_SECTIONS[0];

  const sectionCount = (item: HealthSection) => {
    if (item.id === 'records') {
      return (counts.tests ?? 0) + (counts.vaccinations ?? 0) + (counts.treatments ?? 0);
    }
    if (item.id === 'care') {
      return (counts.workflows ?? 0) + (counts.checkups ?? 0);
    }
    if (item.id === 'reference') {
      return (counts.diseases ?? 0) + (counts.medicines ?? 0);
    }
    if (item.id === 'overview' && counts.alerts) return counts.alerts;
    return counts[item.id];
  };

  return (
    <div className="space-y-3">
      <div className="glass-panel rounded-2xl p-2">
        <nav className="flex flex-wrap gap-1" aria-label="Health sections">
          {HEALTH_SECTIONS.map((item) => {
            const count = sectionCount(item);
            const selected = section === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSectionChange(item.id, item.views?.[0]?.id)}
                className={classNames(
                  'tab-pill flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50',
                  selected ? 'tab-pill-active' : '',
                )}
                title={item.description}
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                <span>{item.name}</span>
                {typeof count === 'number' && count > 0 && (
                  <span
                    className={classNames(
                      'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold',
                      selected ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-200/80 text-slate-700',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {activeSection.views && activeSection.views.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-1">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {activeSection.name}
          </span>
          <div className="flex flex-wrap gap-1">
            {activeSection.views.map((sub) => {
              const subCount = counts[sub.id];
              const selected = view === sub.id;
              return (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => onSectionChange(section, sub.id)}
                  className={classNames(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50',
                    selected
                      ? 'bg-cyan-600 text-white shadow-sm'
                      : 'bg-white/70 text-slate-600 hover:bg-white hover:text-slate-900',
                  )}
                >
                  {sub.name}
                  {typeof subCount === 'number' && subCount > 0 && (
                    <span className="ml-1.5 text-xs opacity-80">({subCount})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
